// src/components/AlignToolbar.js

import { useCallback } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  AlignHorizontalLeft,
  AlignHorizontalCenter,
  AlignHorizontalRight,
  AlignVerticalTop,
  AlignVerticalCenter,
  AlignVerticalBottom,
} from "@mui/icons-material";
import { ActiveSelection } from "fabric";
import useCanvasHistory from "../hooks/useCanvasHistory";

const AlignToolbar = ({ canvas }) => {
  const { saveHistory } = useCanvasHistory(canvas);

  const alignObjects = useCallback(
    (direction) => {
      if (!canvas) {
        console.error("AlignToolbar: Canvas is not available");
        return;
      }

      const activeSelection = canvas.getActiveObject();
      if (!activeSelection || !(activeSelection instanceof ActiveSelection)) {
        console.warn("AlignToolbar: No valid ActiveSelection");
        return;
      }

      // Get the initial bounding box (excluding stroke/shadow and padding)
      const bound = activeSelection.getBoundingRect(true, true);
      const zoom = canvas.getZoom() || 1;

      // Get the ActiveSelection's group offset
      const groupLeft = activeSelection.left;
      const groupTop = activeSelection.top;

      // Use bounding box coordinates in object space
      const boundLeft = bound.left;
      const boundTop = bound.top;
      const boundWidth = bound.width;
      const boundHeight = bound.height;

      console.log("AlignToolbar: Aligning objects", {
        direction,
        bound: {
          left: boundLeft,
          top: boundTop,
          width: boundWidth,
          height: boundHeight,
        },
        groupOffset: { left: groupLeft, top: groupTop },
        objectCount: activeSelection.getObjects().length,
        zoom,
      });

      activeSelection.forEachObject((obj) => {
        // Get object’s bounding rect (excluding stroke/shadow and padding)
        const objBound = obj.getBoundingRect(true, true);
        const objWidth = objBound.width;
        const objHeight = objBound.height;

        // Objects have originX: "center" and originY: "center"
        // Calculate current canvas-space positions (before alignment)
        const currentCanvasLeft = obj.left + groupLeft;
        const currentCanvasTop = obj.top + groupTop;

        let newCanvasLeft, newCanvasTop;

        // Calculate new positions in canvas space, aligning edges to bounding box
        switch (direction) {
          case "left":
            newCanvasLeft = boundLeft; // Align left edge to bound.left
            newCanvasTop = currentCanvasTop; // Preserve vertical position
            break;
          case "center":
            newCanvasLeft = boundLeft + boundWidth / 2; // Align center to bound center
            newCanvasTop = currentCanvasTop;
            break;
          case "right":
            newCanvasLeft = boundLeft + boundWidth - objWidth; // Align right edge to bound.right
            newCanvasTop = currentCanvasTop;
            break;
          case "top":
            newCanvasLeft = currentCanvasLeft; // Preserve horizontal position
            newCanvasTop = boundTop; // Align top edge to bound.top
            break;
          case "middle":
            newCanvasLeft = currentCanvasLeft;
            newCanvasTop = boundTop + boundHeight / 2; // Align vertical center to bound middle
            break;
          case "bottom":
            newCanvasLeft = currentCanvasLeft;
            newCanvasTop = boundTop + boundHeight - objHeight; // Align bottom edge to bound.bottom
            break;
          default:
            console.warn("AlignToolbar: Unknown direction", { direction });
            return;
        }

        // Adjust for originX: "center" and originY: "center"
        // Since left/top are the center, adjust to align the edge
        const adjustedCanvasLeft = newCanvasLeft + objWidth / 2;
        const adjustedCanvasTop = newCanvasTop + objHeight / 2;

        // Convert back to group coordinates
        const newLeft = adjustedCanvasLeft - groupLeft;
        const newTop = adjustedCanvasTop - groupTop;

        console.log("AlignToolbar: Object alignment", {
          type: obj.type,
          originalLeft: obj.left,
          originalTop: obj.top,
          originalCanvasLeft: currentCanvasLeft,
          originalCanvasTop: currentCanvasTop,
          newCanvasLeft: adjustedCanvasLeft,
          newCanvasTop: adjustedCanvasTop,
          newLeft,
          newTop,
          objWidth,
          objHeight,
          originX: obj.originX,
          originY: obj.originY,
        });

        // Set new position
        obj.set({ left: newLeft, top: newTop });

        // Update coordinates
        obj.setCoords();
      });

      // Update the ActiveSelection and canvas
      activeSelection.setCoords();
      canvas.requestRenderAll();
      saveHistory(canvas);

      // Deselect all objects
      canvas.discardActiveObject();
      canvas.requestRenderAll();

      console.log("AlignToolbar: Alignment complete and objects deselected", {
        direction,
      });
    },
    [canvas, saveHistory]
  );

  const directions = [
    {
      name: "Horizontal Left",
      icon: <AlignHorizontalLeft />,
      direction: "left",
    },
    {
      name: "Horizontal Center",
      icon: <AlignHorizontalCenter />,
      direction: "center",
    },
    {
      name: "Horizontal Right",
      icon: <AlignHorizontalRight />,
      direction: "right",
    },
    { name: "Vertical Top", icon: <AlignVerticalTop />, direction: "top" },
    {
      name: "Vertical Center",
      icon: <AlignVerticalCenter />,
      direction: "middle",
    },
    {
      name: "Vertical Bottom",
      icon: <AlignVerticalBottom />,
      direction: "bottom",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 1,
        p: 1,
        backgroundColor: "white",
        borderRadius: "4px",
      }}
    >
      {directions.map(({ name, icon, direction }) => (
        <Tooltip
          key={direction}
          title={`Align ${name}`}
          enterDelay={600}
          leaveDelay={200}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#454545",
                color: "#fff",
                fontSize: "12px",
                textAlign: "center",
                "& .MuiTooltip-arrow::before": {
                  backgroundColor: "#454545",
                },
              },
            },
          }}
        >
          <IconButton
            onClick={() => alignObjects(direction)}
            sx={{ color: "primary.main" }}
          >
            {icon}
          </IconButton>
        </Tooltip>
      ))}
    </Box>
  );
};

export default AlignToolbar;
